// Глобальный режим отображения сумм: UZS (как в БД) или USD (конвертация
// на лету по текущему курсу с cbu.uz). Хранится в localStorage чтобы переключение
// сохранялось между сессиями.
//
// Курс приходит из /api/fx/current раз в час (TanStack staleTime). Все UZS-суммы
// конвертируются делением на rate. Транзакции с currency != UZS показываются в
// нативной валюте независимо от тоггла (доллары остаются долларами).

import { useQuery } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api } from '../api/client';
import { formatMinor, formatWithCurrency as rawFormat } from './money';

type ViewCcy = 'UZS' | 'USD';

type Ctx = {
  viewCcy: ViewCcy;
  toggle: () => void;
  // UZS-rate для конверсии. null если ещё не загружен или сеть упала.
  usdRateUzs: number | null;
  // Универсальный форматтер: если amount уже в той же валюте, что и view —
  // показываем как есть. Если view=USD и storage=UZS — конвертируем.
  formatMoney: (amountMinor: bigint | string, storageCcy: string) => string;
  // Короткая запись: 1,5 млн / 750 тыс / 320. Подставляет символ валюты.
  formatShortMoney: (amountMinorStr: string) => string;
  // То же самое БЕЗ единицы измерения.
  //
  // Единица принадлежит столбцу, а не каждой ячейке. В списке из десяти строк
  // «сум» десять раз — это шум, и он ломает выравнивание: глаз ведёт по
  // последнему знаку, а последним оказывается «м», а не цифра. Валюта уже
  // объявлена бейджем в углу экрана и подписью к главному числу блока.
  formatAmount: (amountMinor: bigint | string, storageCcy: string) => string;
  formatShortAmount: (amountMinorStr: string) => string;
  // Подпись единицы для заголовков блоков: «сум» или «$».
  unitLabel: string;
  // Сырая конверсия в "major" (целая единица). UZS→USD по текущему курсу.
  // Используется для собственных формул отображения.
  convertMajor: (amountMinorStr: string) => { value: number; symbol: string };
};

const STORAGE_KEY = 'rl_view_ccy';

// Единицу отрезаем с хвоста уже отформатированной строки, а не собираем число
// заново: так запись с единицей и без неё гарантированно не разойдутся в
// разрядах и округлении.
const UNIT_TAIL = /\s*(?:сум|\$|USD|EUR|RUB)$/u;

function stripUnit(s: string): string {
  return s.replace(UNIT_TAIL, '');
}

const ViewCurrencyContext = createContext<Ctx | null>(null);

function loadInitial(): ViewCcy {
  if (typeof localStorage === 'undefined') return 'UZS';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'USD' ? 'USD' : 'UZS';
}

export function ViewCurrencyProvider({ children }: { children: ReactNode }): JSX.Element {
  const [viewCcy, setViewCcy] = useState<ViewCcy>(loadInitial());

  // Привязываем queryKey к сегодняшней UTC-дате: после полуночи кеш TanStack
  // авто-инвалидируется и улетит свежий fetch — иначе можно зависнуть на
  // вчерашнем курсе если Mini App открыт через сутки.
  const todayKey = new Date().toISOString().slice(0, 10);
  const fxQ = useQuery({
    queryKey: ['fx', 'current', todayKey],
    queryFn: () => api.fxCurrent(),
    staleTime: 60 * 60 * 1000, // 1 час — внутри дня кешируем
    refetchOnWindowFocus: false,
  });

  const usdRateUzs = fxQ.data?.rates.USD ?? null;

  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, viewCcy);
    }
  }, [viewCcy]);

  const toggle = useCallback(() => {
    setViewCcy((prev) => (prev === 'UZS' ? 'USD' : 'UZS'));
  }, []);

  const formatMoney = useCallback(
    (amountMinor: bigint | string, storageCcy: string): string => {
      // Если транзакция в чужой валюте (например USD/EUR/RUB) — показываем как есть.
      // Тоггл влияет только на конверсию UZS↔USD.
      const ccy = storageCcy.toUpperCase();
      if (viewCcy === 'UZS' || ccy !== 'UZS' || !usdRateUzs || usdRateUzs <= 0) {
        return rawFormat(amountMinor, storageCcy);
      }
      // view=USD, storage=UZS → конвертация: UZS-minor / rate_uzs_per_usd = USD-minor
      // bigint-only через scaled rate.
      const minor = typeof amountMinor === 'string' ? BigInt(amountMinor) : amountMinor;
      const rateScaled = BigInt(Math.round(usdRateUzs * 1_000_000));
      // (UZS_minor × 10^6) / (rate × 10^6) = USD_minor (та же scale=2)
      const usdMinor = (minor * 1_000_000n) / rateScaled;
      return rawFormat(usdMinor, 'USD');
    },
    [viewCcy, usdRateUzs],
  );

  // major value в текущей view-валюте (UZS если view=UZS, USD если view=USD и rate есть).
  const convertMajor = useCallback(
    (amountMinorStr: string): { value: number; symbol: string } => {
      const minor = BigInt(amountMinorStr);
      const majorUzs = Number(minor) / 100;
      if (viewCcy === 'UZS' || !usdRateUzs || usdRateUzs <= 0) {
        return { value: majorUzs, symbol: 'сум' };
      }
      return { value: majorUzs / usdRateUzs, symbol: '$' };
    },
    [viewCcy, usdRateUzs],
  );

  const formatShortMoney = useCallback(
    (amountMinorStr: string): string => {
      const { value, symbol } = convertMajor(amountMinorStr);
      if (value === 0) return `0 ${symbol}`;
      const abs = Math.abs(value);
      const sign = value < 0 ? '−' : '';
      let core: string;
      // «млн»/«тыс», а не «M»/«k»: латинские сокращения внутри русского
      // интерфейса — первое, что выдаёт шаблон.
      if (abs >= 1_000_000) {
        core = `${sign}${(abs / 1_000_000).toFixed(abs < 10_000_000 ? 1 : 0)} млн`;
      } else if (abs >= 1_000) {
        core = `${sign}${(abs / 1_000).toFixed(abs < 10_000 ? 1 : 0)} тыс`;
      } else {
        // Для USD-мелочи показываем до 2 знаков после запятой.
        core = sign + (symbol === '$' ? abs.toFixed(2) : Math.round(abs).toString());
      }
      return `${core} ${symbol}`;
    },
    [convertMajor],
  );

  const formatAmount = useCallback(
    (amountMinor: bigint | string, storageCcy: string): string =>
      stripUnit(formatMoney(amountMinor, storageCcy)),
    [formatMoney],
  );

  const formatShortAmount = useCallback(
    (amountMinorStr: string): string => stripUnit(formatShortMoney(amountMinorStr)),
    [formatShortMoney],
  );

  const unitLabel = viewCcy === 'USD' && usdRateUzs ? '$' : 'сум';

  const value = useMemo<Ctx>(
    () => ({
      viewCcy,
      toggle,
      usdRateUzs,
      formatMoney,
      formatShortMoney,
      formatAmount,
      formatShortAmount,
      unitLabel,
      convertMajor,
    }),
    [
      viewCcy,
      toggle,
      usdRateUzs,
      formatMoney,
      formatShortMoney,
      formatAmount,
      formatShortAmount,
      unitLabel,
      convertMajor,
    ],
  );

  return <ViewCurrencyContext.Provider value={value}>{children}</ViewCurrencyContext.Provider>;
}

export function useViewCurrency(): Ctx {
  const v = useContext(ViewCurrencyContext);
  if (!v) {
    // Fallback вне провайдера — на случай тестов и storybook'ов.
    return {
      viewCcy: 'UZS',
      toggle: () => {},
      usdRateUzs: null,
      formatMoney: rawFormat,
      formatShortMoney: (s: string) => rawFormat(s, 'UZS'),
      formatAmount: (a: bigint | string, c: string) => formatMinor(a, c),
      formatShortAmount: (s: string) => formatMinor(s, 'UZS'),
      unitLabel: 'сум',
      convertMajor: (s: string) => ({ value: Number(BigInt(s)) / 100, symbol: 'сум' }),
    };
  }
  return v;
}
